import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { caseStudies } from '../mockData';
import { Button } from '../components/ui/button';
import { ArrowRight, CheckCircle2 } from 'lucide-react';

const CaseStudyDetail = () => {
  const { id } = useParams();
  const caseStudy = caseStudies.find(cs => cs.id === id);

  if (!caseStudy) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Case Study Not Found</h1>
          <Button asChild>
            <Link to="/client-success">View All Success Stories</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800 text-white py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm font-semibold text-indigo-200 mb-4">{caseStudy.industry}</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">{caseStudy.title}</h1>
          <div className="text-lg text-indigo-100">Client: {caseStudy.client}</div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Challenge */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">The Challenge</h2>
          <div className="bg-red-50 rounded-2xl p-8 border-l-4 border-red-500">
            <p className="text-gray-800 text-lg leading-relaxed">{caseStudy.challenge}</p>
          </div>
        </section>

        {/* Solution */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Solution</h2>
          <div className="bg-indigo-50 rounded-2xl p-8 border-l-4 border-indigo-500">
            <p className="text-gray-800 text-lg leading-relaxed">{caseStudy.solution}</p>
          </div>
        </section>

        {/* Results */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Results & Impact</h2>
          <div className="bg-green-50 rounded-2xl p-8">
            <div className="space-y-4">
              {caseStudy.results.map((result, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <p className="text-gray-800 text-lg">{result}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technologies */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Technologies Used</h2>
          <div className="flex flex-wrap gap-3">
            {caseStudy.technologies.map((tech, index) => (
              <span
                key={index}
                className="px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-medium rounded-xl"
              >
                {tech}
              </span>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">Ready for Similar Results?</h2>
          <p className="text-xl text-indigo-100 mb-8">
            Let's discuss how we can help transform your business
          </p>
          <Button
            asChild
            size="lg"
            className="bg-white text-indigo-600 hover:bg-gray-100 px-8 rounded-xl shadow-xl"
          >
            <Link to="/contact" className="flex items-center space-x-2">
              <span>Get Started</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </Button>
        </section>
      </div>
    </div>
  );
};

export default CaseStudyDetail;